import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
const _excluded = ["search"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Button, Checkbox, Chip, FormControlLabel, MenuItem, Paper, Snackbar, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { track } from 'src/configs/amplitude';
import { WorkPanel } from './WorkPanel';
import { DueCell } from './DueCell';
import { ChangesView } from './ChangesView';
import { EnrolmentBanner } from './EnrolmentBanner';
import { LANE_TABS, STAGES, formatCycle, laneOf, laneQuery, nextItem, previousItem, statusOf, tabCounts, workStateLabel } from './queue';
import { answered } from 'src/components/property/UtilityPortalCredentials/asyncResult';
import { isLivableStaffFromToken } from 'src/utils/isLivableStaffFromToken';
const EMPTY = {
  items: [],
  total: 0,
  facets: null,
  unseenEnrolments: 0,
  heldLookupFailed: false
};
const DEFAULT_VIEW = {
  lane: 'ApPush',
  stage: '',
  status: '',
  companyId: '',
  providerName: '',
  utilityType: '',
  search: '',
  includeNotEnrolled: false,
  includeDeadEnds: false,
  sortModel: [],
  page: 0,
  pageSize: 100
};
const TILES = [{
  label: 'Ready to allocate',
  stage: 'ReadyToAllocate',
  status: ''
}, {
  label: 'Late',
  stage: '',
  status: 'Late',
  alarm: true
}, {
  label: 'Logins failing',
  stage: '',
  status: 'LoginFailing',
  alarm: true
}, {
  label: 'Ready to approve',
  stage: 'ReadyToApprove',
  status: ''
}];

/** A dropdown of the lane's own values with their counts. */
const FacetSelect = _ref => {
  let label = _ref.label,
    allLabel = _ref.allLabel,
    value = _ref.value,
    options = _ref.options,
    onChange = _ref.onChange;
  return /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    label: label,
    value: value,
    onChange: e => onChange(e.target.value),
    sx: {
      minWidth: 170
    }
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, allLabel), (options || []).map(option => /*#__PURE__*/React.createElement(MenuItem, {
    key: option.key,
    value: option.key
  }, "".concat(option.label, " (").concat(option.count, ")"))));
};

/**
 * The billing team's work queue: utilities with AP Push or Bill Automation on, in the order to work them.
 *
 * Rendered as a tab on the admin page and also mounted at /bill-queue, which the Livy palette links to. Box rather
 * than Container for that reason: nested inside the admin page's own Container the grid would sit inset from the tabs.
 * Sorting, filtering and paging are the server's: a lane can hold thousands of rows and the Lambda answers in 6 MB.
 */
export const BillQueuePage = observer(() => {
  const store = useStore();
  const _useState = useState(EMPTY),
    _useState2 = _slicedToArray(_useState, 2),
    page = _useState2[0],
    setPage = _useState2[1];
  const _useState3 = useState(DEFAULT_VIEW),
    _useState4 = _slicedToArray(_useState3, 2),
    view = _useState4[0],
    setView = _useState4[1];
  const _useState5 = useState({
      loading: true,
      error: '',
      toast: ''
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    state = _useState6[0],
    setState = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    selectedId = _useState8[0],
    setSelectedId = _useState8[1];
  // The open row as last seen: saving a bill can move it off this page, and the panel stays on it regardless.
  const lastSelected = useRef(null);
  // Bumped per load, so the open panel re-reads its row after every action.
  const _useState9 = useState(0),
    _useState0 = _slicedToArray(_useState9, 2),
    loadCount = _useState0[0],
    setLoadCount = _useState0[1];
  const _useState1 = useState(0),
    _useState10 = _slicedToArray(_useState1, 2),
    changesKey = _useState10[0],
    setChangesKey = _useState10[1];
  // `run()` cancels the in-flight request every time it is called and the model swallows the cancel, so a stale
  // response must not paint over a newer one.
  const requestSeq = useRef(0);

  // From the token, not the store view: the localStorage mirror is written after the first render on a cold load and
  // is not observable, so the gate settled on "not staff" and stayed there.
  const isStaff = isLivableStaffFromToken();

  // Debounced separately: every keystroke used to refire a cross-company query.
  const _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    debouncedSearch = _useState12[0],
    setDebouncedSearch = _useState12[1];
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(view.search), 300);
    return () => clearTimeout(timer);
  }, [view.search]);
  const isChanges = view.lane === 'Changes';
  // Keyed on everything but the raw search, so a keystroke waits for the debounce instead of querying.
  const typedSearch = view.search,
    filters = _objectWithoutProperties(view, _excluded);
  const filtersKey = JSON.stringify(filters);
  const query = useMemo(() => laneQuery(_objectSpread(_objectSpread({}, JSON.parse(filtersKey)), {}, {
    search: debouncedSearch
  })), [filtersKey, debouncedSearch]);
  const load = useCallback(async () => {
    if (isChanges) return;
    const seq = ++requestSeq.current;
    setState(s => _objectSpread(_objectSpread({}, s), {}, {
      loading: true,
      error: ''
    }));
    const resp = await store.admin.getBillQueueAsync.run(query);
    if (seq !== requestSeq.current) return; // superseded; the newer request owns the state
    const data = (resp === null || resp === void 0 ? void 0 : resp.response) || resp;
    // An empty queue and a failed request look the same without this. "Nothing to work" is the one message an
    // operator must never be shown wrongly.
    if (!answered(resp) || store.admin.getBillQueueAsync.isError || !Array.isArray(data === null || data === void 0 ? void 0 : data.items)) {
      setPage(EMPTY);
      setState(s => _objectSpread(_objectSpread({}, s), {}, {
        loading: false,
        error: 'Could not load the Bill Queue.'
      }));
      return;
    }
    setPage(_objectSpread(_objectSpread({}, EMPTY), data));
    setState(s => _objectSpread(_objectSpread({}, s), {}, {
      loading: false
    }));
    setLoadCount(c => c + 1);
  }, [store, query, isChanges]);
  useEffect(() => {
    if (isStaff) load();
  }, [isStaff, load]);

  // Allocating and approving happen in another tab: coming back re-reads the queue, so its stages are current.
  useEffect(() => {
    if (!isStaff) return undefined;
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [isStaff, load]);
  useEffect(() => {
    if (isStaff) track('bill-queue-viewed', {
      lane: view.lane
    });
  }, [isStaff, view.lane]);
  const update = (patch, filter) => {
    setView(v => _objectSpread(_objectSpread({}, v), {}, {
      page: 0
    }, patch));
    if (filter) track('bill-queue-filtered', {
      filter
    });
  };
  const selected = useMemo(() => {
    var _lastSelected$current;
    if (!selectedId) return null;
    const found = page.items.find(i => i.allocationTableId === selectedId);
    if (found) lastSelected.current = found;
    return found || (((_lastSelected$current = lastSelected.current) === null || _lastSelected$current === void 0 ? void 0 : _lastSelected$current.allocationTableId) === selectedId ? lastSelected.current : null);
  }, [page.items, selectedId]);

  // The server has the ack; clear the banner now and re-read both views rather than wait on the next load.
  const handleSeen = () => {
    setPage(p => _objectSpread(_objectSpread({}, p), {}, {
      unseenEnrolments: 0
    }));
    setChangesKey(k => k + 1);
    load();
  };
  const handleOpen = row => {
    lastSelected.current = row;
    setSelectedId(row.allocationTableId);
    track('bill-queue-row-opened', {
      lane: row.lane,
      stage: row.stage,
      status: row.status
    });
  };

  /**
   * Runs an async model action and reports what actually happened. `run()` never rejects: asyncModel records the error
   * on the model and resolves undefined, so success is read from `isError`, not from a try/catch.
   */
  const runAndReport = async (asyncModel, action, successToast, failureToast) => {
    await action();
    const failed = asyncModel.isError;
    setState(s => _objectSpread(_objectSpread({}, s), {}, {
      toast: failed ? failureToast : successToast
    }));
    await load();
    return !failed;
  };
  const handleClaim = item => runAndReport(store.admin.claimBillQueueItemAsync, () => store.admin.claimBillQueueItemAsync.run({
    allocationTableId: item.allocationTableId
  }), 'Claimed.',
  // A 409 is the normal outcome of two people clicking at once, not a fault to shout about.
  'Someone else claimed this first.');
  const handleRelease = item => runAndReport(store.admin.releaseBillQueueItemAsync, () => store.admin.releaseBillQueueItemAsync.run(item.allocationTableId), 'Released.', 'Could not release this row.');
  const handleSetState = (item, workState, note) => runAndReport(store.admin.setBillQueueStateAsync, () => store.admin.setBillQueueStateAsync.run({
    allocationTableId: item.allocationTableId,
    workState,
    note
  }), workState === 'ErrorWithAllocation' ? 'Sent back to allocation.' : "Marked ".concat(workStateLabel(workState).toLowerCase(), "."), 'Could not update this row.');
  const handleEnterBill = async (item, entry) => {
    const saved = await store.admin.enterBillQueueBillAsync.run(_objectSpread({
      allocationTableId: item.allocationTableId
    }, entry));
    // run() resolves undefined on an API failure; throw so the drawer keeps the row open with the server's wording.
    // It does not claim the bill was not saved: the create commits before the allocation is triggered.
    if (store.admin.enterBillQueueBillAsync.isError || !saved) {
      var _store$admin$enterBil;
      const failure = (_store$admin$enterBil = store.admin.enterBillQueueBillAsync.error) === null || _store$admin$enterBil === void 0 ? void 0 : _store$admin$enterBil.errorResp;
      throw new Error([failure === null || failure === void 0 ? void 0 : failure.title, ...((failure === null || failure === void 0 ? void 0 : failure.body) || [])].filter(Boolean).join(' ') || 'Could not confirm the bill. Close this and reopen the row before trying again.');
    }
    setState(s => _objectSpread(_objectSpread({}, s), {}, {
      toast: 'Bill saved. Sent to QA for review.'
    }));
    // The row stays on the queue as Ready to approve, so the panel stays on it.
    await load();
  };
  const facets = page.facets || {};
  const counts = tabCounts(facets.lanes);
  const stageCount = key => {
    var _facets$stages;
    return ((_facets$stages = facets.stages) === null || _facets$stages === void 0 ? void 0 : _facets$stages[key]) || 0;
  };
  const statusCount = key => {
    var _facets$statuses;
    return ((_facets$statuses = facets.statuses) === null || _facets$statuses === void 0 ? void 0 : _facets$statuses[key]) || 0;
  };
  const columns = useMemo(() => [{
    field: 'propertyAddress',
    headerName: 'Property',
    flex: 1.4,
    minWidth: 240,
    renderCell: _ref2 => {
      let row = _ref2.row;
      return /*#__PURE__*/React.createElement(Stack, {
        sx: {
          paddingY: '6px',
          minWidth: 0
        }
      }, /*#__PURE__*/React.createElement(Stack, {
        direction: "row",
        spacing: 6,
        alignItems: "center"
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        noWrap: true
      }, row.propertyAddress || row.propertyName), row.isNewlyEnrolled ? /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        color: "error",
        label: "New",
        sx: {
          height: 18
        }
      }) : null), /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        color: "text.secondary",
        noWrap: true
      }, row.companyName));
    }
  }, {
    field: 'utilityTypeName',
    headerName: 'Utility',
    width: 110
  }, {
    field: 'providerName',
    headerName: 'Provider',
    flex: 1,
    minWidth: 160
  }, {
    field: 'lane',
    headerName: 'Lane',
    width: 230,
    sortable: false,
    renderCell: _ref3 => {
      let row = _ref3.row;
      return /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        variant: "outlined",
        color: laneOf(row.lane).color,
        label: laneOf(row.lane).label
      });
    }
  }, {
    field: 'stage',
    headerName: 'Stage',
    width: 150,
    valueGetter: _ref4 => {
      let row = _ref4.row;
      return STAGES[row.stage] || row.stage;
    }
  }, {
    field: 'status',
    headerName: 'Status',
    width: 180,
    renderCell: _ref5 => {
      let row = _ref5.row;
      return /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        color: statusOf(row.status).color,
        label: statusOf(row.status).label
      });
    }
  }, {
    field: 'expectedBillDate',
    headerName: 'Expected / received',
    width: 200,
    renderCell: _ref6 => {
      let row = _ref6.row;
      return row.stage === 'ReadyToAllocate' && row.receivedOn ? "Received ".concat(new Date(row.receivedOn).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
      })) : /*#__PURE__*/React.createElement(DueCell, {
        row: row
      });
    }
  }, {
    field: 'daysToCutOff',
    headerName: 'Cut-off',
    width: 110,
    valueGetter: _ref7 => {
      let row = _ref7.row;
      return row.statementDate || '';
    },
    renderCell: _ref8 => {
      let row = _ref8.row;
      return row.statementDate || '-';
    }
  }, {
    field: 'cycle',
    headerName: 'Cycle',
    width: 100,
    sortable: false,
    valueGetter: _ref9 => {
      let row = _ref9.row;
      return formatCycle(row.year, row.month);
    }
  }, {
    field: 'assignedToName',
    headerName: 'Who',
    width: 130,
    sortable: false,
    valueGetter: _ref0 => {
      let row = _ref0.row;
      return row.assignedToName || '-';
    }
  }], []);
  if (!isStaff) {
    return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Alert, {
      severity: "error",
      sx: {
        marginTop: '24px'
      }
    }, "This page is for Livable staff."));
  }
  const laneLabel = view.lane === 'ApPush' ? 'AP Push' : 'Bill Automation';
  const emptyText = view.lane === 'BillAutomation' && !view.includeNotEnrolled && !counts.BillAutomation ? "No utilities have Bill Automation turned on yet. Tick 'Include utilities not turned on' to see every utility." : "Nothing to work in ".concat(laneLabel, " right now.");
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingY: '24px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h4"
  }, "Bill Queue"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginY: '8px'
    },
    color: "text.secondary"
  }, "Utilities with AP Push or Bill Automation on, in the order to work them."), /*#__PURE__*/React.createElement(EnrolmentBanner, {
    unseen: page.unseenEnrolments,
    onReview: () => update({
      lane: 'Changes'
    }),
    onSeen: handleSeen
  }), /*#__PURE__*/React.createElement(Tabs, {
    value: view.lane,
    onChange: (_, lane) => update({
      lane,
      stage: '',
      status: '',
      companyId: '',
      providerName: '',
      utilityType: ''
    }),
    variant: "scrollable",
    allowScrollButtonsMobile: true,
    "aria-label": "Bill Queue lanes",
    sx: {
      marginBottom: '12px'
    }
  }, LANE_TABS.map(tab => {
    const count = tab.value === 'Changes' ? page.unseenEnrolments : counts[tab.value];
    const hot = tab.value === 'Changes' ? page.unseenEnrolments > 0 : tab.value === 'ApPush' && counts.urgent > 0;
    return /*#__PURE__*/React.createElement(Tab, {
      key: tab.value,
      value: tab.value,
      label: /*#__PURE__*/React.createElement(Stack, {
        direction: "row",
        spacing: 6,
        alignItems: "center"
      }, /*#__PURE__*/React.createElement("span", null, tab.label), count ? /*#__PURE__*/React.createElement(Chip, {
        size: "small",
        color: hot ? 'error' : 'default',
        label: count,
        sx: {
          height: 20
        }
      }) : null)
    });
  })), isChanges ? /*#__PURE__*/React.createElement(ChangesView, {
    key: changesKey
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, state.error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    action: /*#__PURE__*/React.createElement(Button, {
      color: "inherit",
      size: "small",
      onClick: load
    }, "Try again")
  }, "Could not load the Bill Queue. Counts and rows are hidden until it loads.") : null, page.heldLookupFailed ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginBottom: '12px'
    }
  }, "Could not check for received bills, so some rows may show Waiting when a bill is already in.") : null, state.error ? null : /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        md: 'repeat(4, 1fr)'
      },
      gap: '12px',
      marginBottom: '12px'
    }
  }, TILES.map(tile => {
    const value = tile.stage ? stageCount(tile.stage) : statusCount(tile.status);
    const active = view.stage === tile.stage && view.status === tile.status;
    return /*#__PURE__*/React.createElement(Box, {
      key: tile.label,
      component: "button",
      type: "button",
      onClick: () => update(active ? {
        stage: '',
        status: ''
      } : {
        stage: tile.stage,
        status: tile.status
      }, tile.label)
      // A Box, not a Paper: the theme spaces adjacent Papers 20px apart, which pushed every tile but the first down.
      ,
      sx: {
        width: '100%',
        display: 'block',
        backgroundColor: 'background.paper',
        borderRadius: '4px',
        padding: '12px 16px',
        textAlign: 'left',
        cursor: 'pointer',
        border: '1px solid',
        borderColor: active ? 'primary.main' : 'divider',
        font: 'inherit',
        color: 'inherit'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "caption"
    }, tile.label), /*#__PURE__*/React.createElement(Typography, {
      variant: "h5",
      color: tile.alarm && value ? 'error.main' : 'text.primary'
    }, value));
  })), state.error ? null : /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: '16px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 6,
    flexWrap: "wrap",
    useFlexGap: true,
    sx: {
      marginBottom: '12px'
    }
  }, [['', 'All stages'], ...Object.entries(STAGES)].map(_ref1 => {
    let _ref10 = _slicedToArray(_ref1, 2),
      key = _ref10[0],
      label = _ref10[1];
    return /*#__PURE__*/React.createElement(Chip, {
      key: key || 'all',
      label: "".concat(label, " (").concat(key ? stageCount(key) : Object.values(facets.stages || {}).reduce((a, b) => a + b, 0), ")"),
      color: view.stage === key ? 'primary' : 'default',
      variant: view.stage === key ? 'filled' : 'outlined',
      onClick: () => update({
        stage: key
      }, 'stage')
    });
  })), /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: 8,
    sx: {
      marginBottom: '8px'
    },
    flexWrap: "wrap",
    useFlexGap: true,
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(FacetSelect, {
    label: "Company",
    allLabel: "All companies",
    value: view.companyId,
    options: facets.companies,
    onChange: companyId => update({
      companyId
    }, 'company')
  }), /*#__PURE__*/React.createElement(FacetSelect, {
    label: "Provider",
    allLabel: "All providers",
    value: view.providerName,
    options: facets.providers,
    onChange: providerName => update({
      providerName
    }, 'provider')
  }), /*#__PURE__*/React.createElement(FacetSelect, {
    label: "Utility type",
    allLabel: "All utility types",
    value: view.utilityType,
    options: facets.utilityTypes,
    onChange: utilityType => update({
      utilityType
    }, 'utilityType')
  }), /*#__PURE__*/React.createElement(FacetSelect, {
    label: "Status",
    allLabel: "Any status",
    value: view.status,
    options: Object.entries(facets.statuses || {}).map(_ref11 => {
      let _ref12 = _slicedToArray(_ref11, 2),
        key = _ref12[0],
        count = _ref12[1];
      return {
        key,
        label: statusOf(key).label,
        count
      };
    }),
    onChange: status => update({
      status
    }, 'status')
  }), /*#__PURE__*/React.createElement(TextField, {
    size: "small",
    label: "Search address, company, provider",
    value: typedSearch,
    onChange: e => update({
      search: e.target.value
    }),
    sx: {
      minWidth: 240
    }
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      size: "small",
      checked: view.includeNotEnrolled,
      onChange: e => update({
        includeNotEnrolled: e.target.checked
      }, 'includeNotEnrolled')
    }),
    label: "Include utilities not turned on"
  }), /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      size: "small",
      checked: view.includeDeadEnds,
      onChange: e => update({
        includeDeadEnds: e.target.checked
      }, 'includeDeadEnds')
    }),
    label: "Show closed"
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary",
    sx: {
      display: 'block',
      marginBottom: '8px'
    }
  }, "Counts are for this tab before other filters."), /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    columns: columns,
    rows: page.items,
    rowCount: page.total || 0,
    loading: state.loading,
    getRowId: row => row.allocationTableId,
    getRowHeight: () => 'auto',
    disableRowSelectionOnClick: true,
    onRowClick: _ref13 => {
      let row = _ref13.row;
      return handleOpen(row);
    }
    // Enter on a focused cell opens the row too, so the panel is reachable without a mouse.
    ,
    onCellKeyDown: (_ref14, event) => {
      let row = _ref14.row;
      if (event.key === 'Enter') handleOpen(row);
    },
    sortingMode: "server",
    sortModel: view.sortModel,
    onSortModelChange: sortModel => update({
      sortModel
    }, 'sort'),
    paginationMode: "server",
    paginationModel: {
      page: view.page,
      pageSize: view.pageSize
    },
    onPaginationModelChange: _ref15 => {
      let next = _ref15.page,
        pageSize = _ref15.pageSize;
      return setView(v => _objectSpread(_objectSpread({}, v), {}, {
        page: next,
        pageSize
      }));
    },
    pageSizeOptions: [25, 50, 100],
    disableColumnFilter: true,
    sx: {
      '& .MuiDataGrid-row': {
        cursor: 'pointer'
      }
    },
    slots: {
      noRowsOverlay: () => /*#__PURE__*/React.createElement(Stack, {
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        sx: {
          padding: '24px'
        }
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "body2",
        color: "text.secondary"
      }, view.stage || view.status || view.companyId || view.providerName || view.utilityType || view.search ? 'No rows match these filters.' : emptyText))
    }
  }))), /*#__PURE__*/React.createElement(WorkPanel, {
    item: selected,
    refreshKey: loadCount,
    open: !!selected,
    onClose: () => setSelectedId(null),
    onClaim: handleClaim,
    onRelease: handleRelease,
    onSetState: handleSetState,
    onEnterBill: handleEnterBill,
    onPrevious: () => {
      var _previousItem$allocat, _previousItem;
      return setSelectedId((_previousItem$allocat = (_previousItem = previousItem(page.items, selectedId)) === null || _previousItem === void 0 ? void 0 : _previousItem.allocationTableId) !== null && _previousItem$allocat !== void 0 ? _previousItem$allocat : selectedId);
    },
    onNext: () => {
      var _nextItem$allocationT, _nextItem;
      return setSelectedId((_nextItem$allocationT = (_nextItem = nextItem(page.items, selectedId)) === null || _nextItem === void 0 ? void 0 : _nextItem.allocationTableId) !== null && _nextItem$allocationT !== void 0 ? _nextItem$allocationT : selectedId);
    }
  }), /*#__PURE__*/React.createElement(Snackbar, {
    open: !!state.toast,
    autoHideDuration: 3000,
    onClose: () => setState(s => _objectSpread(_objectSpread({}, s), {}, {
      toast: ''
    })),
    message: state.toast
  }));
});